// deriveCloseUrgency — the ONE answer to "how urgent is this closing time?" (BL-1329).
//
// An IsomorphicService per ADR 010: pure, no I/O, no React, safe inside a
// `useMemo` on every render and callable from the server against the same data.
//
// It returns SEMANTICS, never styling (ADR 030's two-layer model): the caller
// maps `band` to its own dialect — `text-urgent` on the bidder, `--danger-500`
// on the admin console. A colour token in this return value would make the
// shared foundation pick between two deliberately different dialects.
//
// `now` is a caller-supplied clock rather than an internal `Date.now()`. That
// keeps the function pure, lets the caller drive it from the server-anchored
// clock (ADR 024 § 5) instead of a possibly-skewed device, and — the reason it
// matters at scale — lets one list-level 1 Hz tick drive every row rather than
// one timer per row (ADR 053).
//
// Before this existed, the form/band decision was made independently in six
// places across four workspaces using five thresholds, two of which matched only
// by coincidence. The thresholds now live in `close-windows.ts` and the policy
// built on top of them lives here.

import { COUNTDOWN_WINDOW_MS, CLOSING_SOON_MS } from './close-windows';
import { timeRemaining } from './time-remaining';
import type { TimeInput, TimeRemaining } from './types';

/**
 * Which shape the caller should render the close time as.
 *
 * `ended` is the hand-off state, NOT "closed": the client never declares a
 * lot closed on its own — it shows this state and waits for the realtime
 * snapshot to confirm closed or extended (ADR 024 § 5). There is deliberately
 * no `closed` member.
 */
export type CloseUrgencyForm =
    /** No close time, or an unparseable one — render nothing. */
    | 'none'
    /** Beyond the countdown window — render the day and time. */
    | 'absolute'
    /** Inside the countdown window — render a live countdown. */
    | 'countdown'
    /** Past the close, or a lot the caller already knows is terminal. */
    | 'ended';

/** Which urgency treatment the caller's dialect should apply. */
export type CloseUrgencyBand = 'normal' | 'closing-soon';

/** The discriminated verdict every countdown surface reads. */
export interface CloseUrgencyVerdict {
    form: CloseUrgencyForm;
    band: CloseUrgencyBand;
    /** True while the parent auction is paused — the clock is held (BUG-1166). */
    frozen: boolean;
    /**
     * True while the remaining time is inside the caller-supplied
     * {@link DeriveCloseUrgencyInput.flashWindowMs} — BL-2592's stronger
     * countdown-flash cue. A STRICT SUBSET of `band === 'closing-soon'`:
     * never true on its own, and never true while {@link frozen} (a held
     * clock face does not flash). Deliberately a sibling field rather than a
     * third `band` member — a third member would silently change every
     * existing `band === 'closing-soon'` equality check (e.g. the clerk
     * console's `deriveClerkLotStatus`), which this card never asked for.
     */
    flashing: boolean;
    /** The breakdown to render, or `null` when there is no clock (`form: 'none'`). */
    remaining: TimeRemaining | null;
}

export interface DeriveCloseUrgencyInput {
    /** The lot's scheduled close. `null` when unscheduled. */
    closeTime: TimeInput | null;
    /** Server-anchored "now", epoch ms. Read fresh each tick (ADR 024 § 5). */
    now: number;
    /**
     * The parent auction's pause instant. While set, the countdown FREEZES here
     * rather than ticking toward close, and the band is read off the frozen
     * remaining — a held clock face painted urgent would be a lie about a lot
     * whose clock is not running (BUG-1166).
     */
    pausedAt?: TimeInput | null;
    /**
     * Set when the caller already knows the lot reached a terminal disposition
     * — a clerk hammered it before its clock ran out. Forces `ended` so the
     * surface stops counting down to a close that will never arrive.
     */
    ended?: boolean;
    /**
     * The "flash in the final minutes" window, in ms — BL-2592. Always the
     * CALLER's own stamped `flashWindowSeconds * 1000` (the auction's own
     * column, read off the realtime snapshot), never a literal here or at the
     * call site (`closingband/no-closing-threshold-literal`). `null` /
     * `undefined` (unknown, or a house/auction with the flash off) never
     * sets {@link CloseUrgencyVerdict.flashing}.
     */
    flashWindowMs?: number | null;
}

/**
 * Decide which form to render a close time as, and which urgency band to paint.
 *
 * @param input.closeTime The lot's scheduled close, or `null`.
 * @param input.now Server-anchored epoch ms.
 * @param input.pausedAt The auction's pause instant, or `null`.
 * @param input.ended Whether the caller already knows the lot is terminal.
 * @param input.flashWindowMs The "flash in the final minutes" window, in ms (BL-2592).
 * @returns The {@link CloseUrgencyVerdict} — semantics only, never styling.
 */
export function deriveCloseUrgency(input: DeriveCloseUrgencyInput): CloseUrgencyVerdict {
    const { closeTime, now, pausedAt = null, ended = false, flashWindowMs = null } = input;

    const frozen = pausedAt != null;

    if (closeTime == null) {
        return { form: 'none', band: 'normal', frozen, flashing: false, remaining: null };
    }

    // `timeRemaining` owns every piece of duration arithmetic in this repo,
    // including the pause anchor. Nothing here recomputes a duration — that
    // would be a second implementation of the thing this file exists to have
    // one of.
    const remaining = timeRemaining({ end: closeTime, now, pausedAt });

    // An unparseable end reads as `ended` with a zero duration, which is
    // indistinguishable from a genuinely-closed lot. Separate them here so a bad
    // value renders nothing rather than a permanent ended state.
    if (!Number.isFinite(toMs(closeTime))) {
        return { form: 'none', band: 'normal', frozen, flashing: false, remaining: null };
    }

    if (ended || remaining.status === 'ended') {
        return { form: 'ended', band: 'normal', frozen, flashing: false, remaining };
    }

    // `<` (not `<=`) at the 24h boundary, so a lot EXACTLY 24h out renders as a
    // date. `LotBidPanel`'s complementary `>=` gate is written to agree with it
    // at that instant; the two boundaries are a matched pair, not independent
    // choices, and moving one without the other re-opens the disagreement.
    if (remaining.totalMs >= COUNTDOWN_WINDOW_MS) {
        return { form: 'absolute', band: 'normal', frozen, flashing: false, remaining };
    }

    // `<=` at the urgency boundary, matching `deriveClerkLotStatus`'s
    // long-standing `msToClose <= CLOSING_SOON_MS`. Deliberately the opposite
    // inclusivity from the window gate above: that one separates two FORMS and
    // must not double-count, this one decides when a treatment STARTS and
    // should include its own threshold instant.
    const band: CloseUrgencyBand = remaining.totalMs <= CLOSING_SOON_MS ? 'closing-soon' : 'normal';

    // BL-2592: flashing is a STRICT SUBSET of closing-soon, on the same `<=`
    // inclusivity (the flash window's own threshold instant counts as inside
    // it), and never true while frozen — a held clock face reads urgent but
    // does not flash (same reasoning BUG-1166 applied to `band` itself).
    const flashing =
        !frozen && band === 'closing-soon' && flashWindowMs != null && remaining.totalMs <= flashWindowMs;

    return { form: 'countdown', band, frozen, flashing, remaining };
}

/** Epoch ms for a {@link TimeInput}, or `NaN` when it cannot be parsed. */
function toMs(value: TimeInput): number {
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    return Date.parse(value);
}
